import React, { useRef, useState } from "react";
import Toast from "../../common/Toast/Toast";
import { CloudUploadIcon } from "../icons";

function LogoUpload({
  id,
  onFileUpload,
  logoHeading,
  logoSubHeading,
  acceptedExtensions = ["png", "jpeg", "jpg"],
  acceptedMimeTypes = ["image/png", "image/jpeg", "image/jpg"],
  imageFormatSizeKey = "settings:image_format_size",
  validImageFormatKey = "settings:valid_image_format",
  t,
}) {
  const fileInputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    processFile(file, event.target);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const file = event.dataTransfer.files[0];
    processFile(file, fileInputRef.current);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const processFile = (file, inputElement) => {
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    const isValidType = acceptedExtensions.includes(extension);
    const isValidMime =
      !file.type || acceptedMimeTypes.includes(file.type.toLowerCase());

    if (isValidType && isValidMime) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: t("settings:file_size_less_than_5mb"),
        });
        if (inputElement) {
          inputElement.value = "";
        }
        return;
      }
      if (inputElement) {
        inputElement.value = "";
      }
      void Promise.resolve()
        .then(() => onFileUpload(file))
        .catch(() => {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:error_occurred"),
          });
        });
      return;
    }

    Toast({
      type: "error",
      title: t("settings:error"),
      message: t(validImageFormatKey),
    });
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const uploadLabel = `${logoHeading} ${logoSubHeading}`.trim();

  return (
    <div
      className={`flex gap-4 py-6 px-4 mt-2 cursor-pointer text-gray-500 bg-white rounded-lg border-2 border-dotted border-gray-300 shadow-sm focus:outline-none focus-visible:border-cfPrimaryButton focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
        dragActive ? "bg-gray-100" : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={openFilePicker}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={uploadLabel}
      aria-describedby={`${id}-dropzone-hint`}
      id={`${id}-dropzone`}
    >
      <input
        type="file"
        accept={acceptedExtensions.map((extension) => `.${extension}`).join(",")}
        onChange={handleFileChange}
        className="hidden"
        id={id}
        ref={fileInputRef}
        tabIndex={-1}
        aria-hidden="true"
      />
      <CloudUploadIcon
        size={36}
        className="shrink-0 my-auto mr-4 inline-flex text-indigo-500"
        aria-hidden="true"
      />
      <div className="flex flex-col">
        <div className="text-sm">
          <span className="font-semibold text-indigo-700">
            {logoHeading} {logoSubHeading}
          </span>
        </div>
        <div id={`${id}-dropzone-hint`} className="mt-2 text-xs">
          {t(imageFormatSizeKey)}
        </div>
      </div>
    </div>
  );
}

export default LogoUpload;
